const SUCCESS_STATUS_MIN = 200;
const SUCCESS_STATUS_END = 300;

export interface PutFileOptions {
  readonly signal?: AbortSignal;
  onProgress?(loaded: number, total: number): void;
}

/**
 * PUTs a file to a pre-signed storage URL. Uses XMLHttpRequest because fetch
 * has no upload progress events. No Authorization header is sent.
 */
export function putFileWithProgress(
  url: string,
  file: File,
  options: PutFileOptions = {}
): Promise<void> {
  const { signal, onProgress } = options;
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = (): void => xhr.abort();
    const settle = (): void => signal?.removeEventListener('abort', onAbort);

    xhr.open('PUT', url);
    xhr.setRequestHeader(
      'Content-Type',
      file.type || 'application/octet-stream'
    );
    xhr.upload.onprogress = (event): void => {
      onProgress?.(
        event.loaded,
        event.lengthComputable ? event.total : file.size
      );
    };
    xhr.onload = (): void => {
      settle();
      if (xhr.status >= SUCCESS_STATUS_MIN && xhr.status < SUCCESS_STATUS_END) {
        onProgress?.(file.size, file.size);
        resolve();
      } else {
        reject(
          new Error(`Failed to upload Gaussian splat file (${xhr.status})`)
        );
      }
    };
    xhr.onerror = (): void => {
      settle();
      reject(new Error('Failed to upload Gaussian splat file (network error)'));
    };
    xhr.onabort = (): void => {
      settle();
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    xhr.send(file);
  });
}
